import React, { useEffect, useState } from "react";
import {
  FileText,
  CheckCircle,
  UploadSimple,
  ClipboardText,
  Scales,
  Briefcase,
} from "@phosphor-icons/react";
import PricingCard from "./PricingCard";
import { usePricing } from "../context/PricingContext";
import { useScrollReveal } from "../hooks/useScrollReveal";
import {
  introSection,
  pricingTabs,
  processCardsByTab,
  pricingHeaderByTab,
  pricingPackagesByTab,
} from "../data/pricingData";

const iconMap = {
  FileText,
  CheckCircle,
  UploadSimple,
  ClipboardText,
  Scales,
  Briefcase,
};

const Reveal = ({ children, className = "" }) => {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`${visible ? "anim-fade-up" : "anim-hidden"} max-w-full ${className}`}
    >
      {children}
    </div>
  );
};

const PricingTabsSection = ({
  onCtaClick,
  sectionBg = "bg-white",
  headerEyebrow = "Pricing Plans",
}) => {
  const { activePricingTab, setActivePricingTab, setSelectedPackage } =
    usePricing();

  const [displayTab, setDisplayTab] = useState(activePricingTab);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (activePricingTab === displayTab) return;
    setVisible(false);
    const timeout = setTimeout(() => {
      setDisplayTab(activePricingTab);
      setVisible(true);
    }, 150);
    return () => clearTimeout(timeout);
  }, [activePricingTab]);

  const handleTabSelect = (tabId) => {
    if (tabId !== activePricingTab) {
      setActivePricingTab(tabId);
    }
  };

  const handleTabKeyDown = (e, index) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const nextIndex =
      e.key === "ArrowRight"
        ? (index + 1) % pricingTabs.length
        : (index - 1 + pricingTabs.length) % pricingTabs.length;
    const nextTab = pricingTabs[nextIndex];
    handleTabSelect(nextTab.id);
    document.getElementById(`pricing-tab-${nextTab.id}`)?.focus();
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(
      { packageTitle: pkg.name, packageAmount: pkg.price },
      activePricingTab,
    );
    if (onCtaClick) onCtaClick();
  };

  const processCards = processCardsByTab[displayTab] || [];
  const pricingHeader = pricingHeaderByTab[displayTab] || {};
  const pricingPackages = pricingPackagesByTab[displayTab] || [];

  return (
    <section
      className={`${sectionBg} py-14 md:py-28`}
      data-testid="pricing-tabs-section"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        {/* ─── Introduction ─── */}
        <Reveal className="mx-auto max-w-3xl text-center mb-10 md:mb-14">
          <span className="text-xs font-bold text-lime uppercase tracking-[0.2em]">
            {headerEyebrow}
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
            {introSection.heading}
          </h2>
          <p className="text-gray-600 mt-3 text-sm sm:text-base leading-relaxed">
            {introSection.description}
          </p>
        </Reveal>

        {/* ─── Tabs ─── */}
        <div className="flex justify-center mb-10 md:mb-14">
          <div
            role="tablist"
            aria-label="Pricing plan type"
            className="inline-flex flex-wrap justify-center gap-1 bg-gray-100 rounded-xl p-1.5"
          >
            {pricingTabs.map((tab, index) => (
              <button
                key={tab.id}
                id={`pricing-tab-${tab.id}`}
                role="tab"
                type="button"
                aria-selected={activePricingTab === tab.id}
                aria-controls={`pricing-tabpanel-${tab.id}`}
                tabIndex={activePricingTab === tab.id ? 0 : -1}
                onClick={() => handleTabSelect(tab.id)}
                onKeyDown={(e) => handleTabKeyDown(e, index)}
                className={`px-4 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm font-bold uppercase tracking-wide transition-all duration-300 ${
                  activePricingTab === tab.id
                    ? "bg-primary text-white shadow-md"
                    : "text-gray-600 hover:text-primary"
                }`}
                data-testid={`pricing-tab-btn-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ─── Tab Content ─── */}
        <div
          id={`pricing-tabpanel-${displayTab}`}
          role="tabpanel"
          aria-labelledby={`pricing-tab-${displayTab}`}
          className={`transition-opacity duration-150 ${visible ? "opacity-100" : "opacity-0"}`}
        >
          {/* Process Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-14 md:mb-20">
            {processCards.map((card, i) => {
              const Icon = iconMap[card.icon] || FileText;
              return (
                <div
                  key={i}
                  className="border border-gray-200 rounded-xl p-6 sm:p-8 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-lime/40"
                  data-testid={`pricing-process-card-${displayTab}-${i}`}
                >
                  <div className="w-11 h-11 rounded-lg bg-lime/10 flex items-center justify-center mb-4">
                    <Icon size={22} weight="duotone" className="text-lime" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2">
                    {card.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {card.description}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Pricing Header */}
          <div className="text-center mb-10 md:mb-14">
            <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-extrabold tracking-tight">
              {pricingHeader.title}
            </h3>
            <p className="text-gray-600 mt-2 text-sm sm:text-base">
              {pricingHeader.subtitle}
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pricingPackages.map((pkg, i) => (
              <PricingCard
                key={i}
                {...pkg}
                onCtaClick={() => handlePackageSelect(pkg)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingTabsSection;
